import AgencyApi from '@/api/agency'
import AdvertiserApi from '@/api/AdvertiserApi'
import { SearchResult } from '@/models/Repository'
import { CreativeFolderRepository } from './CreativeFolder'
import { AgencyModel } from '@/models/Agency'
import PermissionUtil from './PermissionUtil'

const assignModel = data => {
  return data.map(o => new AdvertiserModel(o))
}

const getErrorMessage = rawErrMessage => {
  const errors = [
    {
      response: 'given advertiser name already exists in the agency',
      display: '入力された広告主名はすでに登録されています。',
    },
    {
      response:
        'has not registered. Please register these account by received email',
      display:
        '入力された広告主担当者のメールアドレスは既に招待されていますが、未登録です。\nアカウント登録を完了させてから再度実行してください。\n（保存処理はキャンセルされました）',
    },
  ]
  const error = errors.find(
    err => rawErrMessage && rawErrMessage.includes(err.response),
  )

  return error ? error.display : '保存に失敗しました'
}

export class AdvertiserModel {
  constructor(item) {
    const {
      agencyId,
      agencyName,
      anAdvertiserId,
      brandName,
      brandId,
      categoryMasterId,
      categoryName,
      code,
      currency,
      emailAddresses,
      id,
      name,
      ownerProfileId,
      permission,
      status,
      timeZone,
      agencySalesAccountId,
      agencySalesAccountName,
      systemOwnerSalesAccountId,
      systemOwnerSalesAccountName,
    } = item
    this.agencyId = Number(agencyId)
    this.agencyName = agencyName
    this.anAdvertiserId = anAdvertiserId
    this.brandName = brandName
    this.brandId = brandId
    this.categoryMasterId = categoryMasterId
    this.categoryName = categoryName
    this.code = code
    this.currency = currency
    this.emailAddresses = emailAddresses
    this.id = id
    this.name = name
    this.ownerProfileId = ownerProfileId
    this.permission = permission
    this.status = status
    this.timeZone = timeZone
    ;(this.agencySalesAccountId = agencySalesAccountId || undefined),
      (this.agencySalesAccountName = agencySalesAccountName || undefined),
      (this.systemOwnerSalesAccountId = systemOwnerSalesAccountId || undefined)
    this.systemOwnerSalesAccountName = systemOwnerSalesAccountName || undefined
    this.targetingInventoryList = []
    this.agency = null
    this.creativeFolder = null
  }
  get statusString() {
    return this.status === 'active' ? '有効' : '無効'
  }
  get isActive() {
    return this.status === 'active'
  }
  get hasAdminPermission() {
    return this.permission === 'admin'
  }

  isHigherReadonly() {
    return PermissionUtil.isHigherReadonly(this.permission)
  }

  /**
   * 広告主の担当アカウントリストを取得する。
   * @return {Account[]}
   */
  async getAccountList() {
    if (typeof this.id === 'undefined') return []
    try {
      const advertiserId = this.id
      const minPermissionLevel = 'view'
      const result = await AdvertiserApi.getAdvertiserAccountList({
        advertiserId,
        minPermissionLevel,
      })
      return result.items
    } catch (e) {
      console.error(e)
      return []
    }
  }
  static async search({
    keyword,
    minPermissionLevel,
    advertiserStatuses,
    pageNo,
    pageSize,
    sortKey,
    sortDirection,
  }) {
    const query = {
      keyword,
      minPermissionLevel,
      advertiserStatuses,
      pageNo,
      pageSize,
      sortKey,
      sortDirection,
    }
    const result = await AdvertiserApi.searchAdvertiser(query)
    const items = assignModel(result.items)
    return new SearchResult({
      count: result.count,
      pageNo: result.pageNo,
      items,
    })
  }
  async getTargetingInventoryList() {
    if (!this.id)
      throw new Error('未保存の広告主は配信先リストを取得できません')
    try {
      const res = await AdvertiserApi.getTargetingInventoryList(this.id)
      return res.items
    } catch (e) {
      console.error(e)
      throw new Error('配信先リストの取得に失敗しました')
    }
  }
  async fetchTargetingInventoryList() {
    this.targetingInventoryList = await this.getTargetingInventoryList()
  }
  async getCreativeFolders() {
    return await CreativeFolderRepository.getRootByOwnerProfileId(
      this.ownerProfileId,
    )
  }
  /**
   * クリエイティブフォルダーを検索し、ルートフォルダをセットする。
   */
  async fetchCreativeFolders() {
    this.creativeFolder = await this.getCreativeFolders()
  }
  /**
   * 保持しているクリエイティブフォルダから指定されたIDのものを検索し、返す。
   */
  findCreativeFolder(creativeFolderId) {
    if (this.creativeFolder == null) return null
    return this.creativeFolder.findCreativeFolder(creativeFolderId)
  }

  async getAgency() {
    try {
      const result = await AgencyApi.get(this.agencyId)
      return new AgencyModel(result.item)
    } catch (e) {
      console.error(e)
      throw new Error(`代理店取得に失敗しました`)
    }
  }

  async fetchAgency() {
    this.agency = await this.getAgency()
  }

  static async searchAgency(params) {
    try {
      return await AgencyApi.searchAgency(params)
    } catch (e) {
      console.error(e)
      return []
    }
  }
  static async deleteAdvertiser(id) {
    try {
      return await AdvertiserApi.deleteAdvertiser(id)
    } catch (e) {
      console.error(e)
      return []
    }
  }
}

export class AddAdvertiserForm {
  constructor(item = {}) {
    const {
      agencyId,
      brandId,
      categoryMasterId,
      code,
      currency,
      emailAddresses,
      name,
      timeZone,
      status,
      agencySalesAccountId,
      systemOwnerSalesAccountId,
    } = item
    this.agencyId = Number(agencyId) || undefined
    this.brandId = brandId || undefined
    this.categoryMasterId = categoryMasterId || undefined
    this.code = code || undefined
    this.currency = currency || undefined
    this.emailAddresses = emailAddresses || ['']
    this.name = name || undefined
    this.timeZone = timeZone || undefined
    this.status = status || undefined
    this.agencySalesAccountId = agencySalesAccountId || undefined
    this.systemOwnerSalesAccountId = systemOwnerSalesAccountId || undefined
  }
  /**
   * 広告主を追加する。
   * 成功した場合、続けてクリエイティブルートフォルダを作成する。
   *
   * @return {AdvertiserModel}
   */
  async addAdvertiser() {
    try {
      const result = await AdvertiserApi.addAdvertiser(this)
      const advertiser = new AdvertiserModel(result.data.item)
      await CreativeFolderRepository.create({
        ownerProfileId: advertiser.ownerProfileId,
        name: advertiser.name,
        isRoot: true,
      })
      return advertiser
    } catch (e) {
      throw new Error(getErrorMessage(e.response.data.message))
    }
  }
}

export class EditAdvertiserForm {
  constructor(item = {}) {
    const {
      agencyId,
      agencyName,
      anAdvertiserId,
      brandName,
      brandId,
      categoryMasterId,
      categoryName,
      code,
      currency,
      emailAddresses,
      name,
      timeZone,
      status,
      agencySalesAccountId,
      systemOwnerSalesAccountId,
    } = item
    this.agencyId = Number(agencyId) || null
    this.agencyName = agencyName || null
    this.anAdvertiserId = Number(anAdvertiserId) || null
    this.brandId = brandId || null
    this.brandName = brandName || null
    this.categoryMasterId = categoryMasterId || null
    this.categoryName = categoryName || null
    this.code = code || null
    this.currency = currency || null
    this.emailAddresses = emailAddresses || ['']
    this.name = name || null
    this.timeZone = timeZone || null
    this.status = status || null
    this.agencySalesAccountId = agencySalesAccountId || null
    this.systemOwnerSalesAccountId = systemOwnerSalesAccountId || null
  }
  async editAdvertiser(id) {
    try {
      const res = await AdvertiserApi.editAdvertiser(id, this)
      return res.data.item
    } catch (e) {
      throw new Error(getErrorMessage(e.response.data.message))
    }
  }
}
